import React, { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { Container, Row, Col, Card, Button, Form, Alert } from 'react-bootstrap';
import { AgentPermissionForm } from './AgentPermissionForm';

/**
 * UserCreate component — admin-only form for creating new agents/managers/auditors
 * Optionally provisions email alias + SMTP credentials via the provisioning API
 */
export function UserCreate() {
  const navigate = useNavigate();
  const { apiCall } = useOutletContext();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    personal_email: '',
    phone: '',
    password: '',
    role: 'manager',
    store_id: '',
    can_delete: false,
    view_store_ids: [],
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const isInvestor = formData.role === 'investor';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    setLoading(true);

    try {
      // Investors are not associated with a store and get no permissions/alias
      const payload = isInvestor
        ? { name: formData.name, email: formData.email, phone: formData.phone, password: formData.password, role: 'investor' }
        : formData;
      const response = await apiCall(`${API_BASE_URL}/agents`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      setResult(response);
    } catch (err) {
      setError(err.message || 'Failed to create agent');
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    const agent = result.data;
    const prov = result.emailProvisioning;

    return (
      <Container fluid className="py-4">
        <Row className="mb-3">
          <Col>
            <Button variant="link" onClick={() => navigate('/dashboard/settings/users')} className="p-0">
              &larr; Back to Users
            </Button>
          </Col>
        </Row>

        <Alert variant="success">
          <Alert.Heading>Agent Created Successfully</Alert.Heading>
          <p><strong>{agent.name}</strong> ({agent.email}) has been created as <strong>{agent.role}</strong>.</p>
        </Alert>

        {prov && !prov.error && prov.smtp && (
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Email &amp; SMTP Credentials</h5>
            </Card.Header>
            <Card.Body>
              <p>
                Emails to <strong>{agent.email}</strong> will be forwarded to <strong>{formData.personal_email}</strong>.
              </p>
              <p>To send as <strong>{agent.email}</strong> from Gmail, use these SMTP settings:</p>
              <table className="table table-sm table-bordered" style={{ maxWidth: '500px' }}>
                <tbody>
                  <tr><td><strong>SMTP Server</strong></td><td><code>{prov.smtp.server}</code></td></tr>
                  <tr><td><strong>Port</strong></td><td><code>{prov.smtp.port}</code></td></tr>
                  <tr><td><strong>Username</strong></td><td><code>{prov.smtp.username}</code></td></tr>
                  <tr><td><strong>Password</strong></td><td><code>{prov.smtp.password}</code></td></tr>
                  <tr><td><strong>Security</strong></td><td><code>{prov.smtp.security}</code></td></tr>
                </tbody>
              </table>
              <small className="text-muted">
                Save these credentials — the password cannot be retrieved again.
              </small>
            </Card.Body>
          </Card>
        )}

        {prov && prov.error && (
          <Alert variant="warning">
            Email provisioning failed: {prov.error}. The agent was created but email forwarding was not set up. You can configure it manually later.
          </Alert>
        )}

        <Button variant="primary" onClick={() => navigate('/dashboard/settings/users')}>
          Back to Users
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row className="mb-3">
        <Col>
          <Button variant="link" onClick={() => navigate('/dashboard/settings/users')} className="p-0">
            &larr; Back to Users
          </Button>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col><h2>Create Agent</h2></Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="mb-4">
        <Card.Header><h3>Agent Information</h3></Card.Header>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="Full name"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Business Email <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="e.g. name@tegratauto.com"
                  />
                  <Form.Text>This will be their login email and business address.</Form.Text>
                </Form.Group>
              </Col>
              {!isInvestor && (
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Personal Email <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="email"
                      value={formData.personal_email}
                      onChange={(e) => setFormData({ ...formData, personal_email: e.target.value })}
                      required
                      placeholder="e.g. name@gmail.com"
                    />
                    <Form.Text>Business emails will be forwarded here. SMTP credentials for "Send as" will be generated.</Form.Text>
                  </Form.Group>
                </Col>
              )}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Phone number"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Password <span className="text-danger">*</span></Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Control
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      placeholder="Min 8 chars, uppercase, number, symbol"
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </Button>
                  </div>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Role <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    required
                  >
                    <option value="manager">Manager</option>
                    <option value="auditor">Auditor</option>
                    <option value="admin">Admin</option>
                    <option value="investor">Investor</option>
                  </Form.Select>
                  {isInvestor && (
                    <Form.Text>
                      Investors are not tied to a store. After creating, assign vehicles from the Users list.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>

            {!isInvestor && (
              <AgentPermissionForm
                permissions={formData}
                onChange={setFormData}
              />
            )}

            <div className="mt-3">
              <Button variant="primary" type="submit" disabled={loading} className="me-2">
                {loading ? 'Creating...' : 'Create Agent'}
              </Button>
              <Button
                variant="secondary"
                onClick={() => navigate('/dashboard/settings/users')}
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
}
